import type { ProjectStatus } from '../projects/types';
import type { Task } from '../tasks/types';

/**
 * The TAP Console status picker (Kyu, 28 Sept): Needs Alex, In Progress, Done
 * on each row of the TAP Console section in My Day, matching what Alex reads
 * on the TAP Console page.
 *
 * READ. Mirrors read_huddle exactly
 * (20270172000100_huddle_comments_carry_faces_and_uploads.sql:82-86), so the
 * two pages cannot disagree: the task's status NAME 'In Progress' is In
 * Progress; the name 'Completed' or a ticked task is Done; anything else,
 * including no status, is Needs Alex. Exact, case-sensitive names, as there.
 *
 * WRITE. Only status_id and is_completed, the pair useMoveTaskStatus already
 * writes together. completed_at and completed_by are stamped by the database
 * trigger (tasks_maintain_timestamps), which is what keeps the
 * tasks_completion_shape CHECK satisfied.
 */

export type HuddleStatusKey = 'queue' | 'in_progress' | 'done';

export const HUDDLE_STATUS_LABELS: Record<HuddleStatusKey, string> = {
  queue: 'Needs Alex',
  in_progress: 'In Progress',
  done: 'Done',
};

export const HUDDLE_STATUS_ORDER: HuddleStatusKey[] = ['queue', 'in_progress', 'done'];

export interface HuddleStatusIntent {
  statusId: string | null;
  isCompleted: boolean;
}

export interface HuddleStatusChoice {
  key: HuddleStatusKey;
  label: string;
  /** Null when the project has no status this choice can write. */
  intent: HuddleStatusIntent | null;
  /** Why it is disabled, for the tooltip. Empty when enabled. */
  reason: string;
}

function statusName(task: Task, statuses: ProjectStatus[]): string | null {
  if (task.status_id === null) return null;
  return statuses.find((status) => status.id === task.status_id)?.name ?? null;
}

/** Same rule, same order of tests, as read_huddle's CASE. */
export function huddleStatusOf(task: Task, statuses: ProjectStatus[]): HuddleStatusKey {
  const name = statusName(task, statuses);
  if (name === 'In Progress') return 'in_progress';
  if (name === 'Completed' || task.is_completed) return 'done';
  return 'queue';
}

function active(statuses: ProjectStatus[]): ProjectStatus[] {
  return statuses
    .filter((status) => status.is_active)
    .slice()
    .sort((left, right) => left.position - right.position);
}

/**
 * What Needs Alex writes: the project's first open status by position that
 * read_huddle does not read as In Progress or Done. That is the rule
 * create_project uses for starter tasks ("open in the first status",
 * 20261231000100_for_review_at_both_levels.sql:243-250), and on a personal
 * project it is Not Started. Null when there is none: a null status is also
 * read as Needs Alex, so the choice still works.
 */
export function needsAlexStatusId(statuses: ProjectStatus[]): string | null {
  return (
    active(statuses).find(
      (status) =>
        !status.is_completed_status && status.name !== 'In Progress' && status.name !== 'Completed',
    )?.id ?? null
  );
}

export function huddleStatusChoices(statuses: ProjectStatus[]): HuddleStatusChoice[] {
  const list = active(statuses);
  const inProgress = list.find((status) => status.name === 'In Progress') ?? null;
  const completed =
    list.find((status) => status.is_completed_status && status.name === 'Completed') ??
    list.find((status) => status.is_completed_status) ??
    null;
  return [
    {
      key: 'queue',
      label: HUDDLE_STATUS_LABELS.queue,
      intent: { statusId: needsAlexStatusId(statuses), isCompleted: false },
      reason: '',
    },
    {
      key: 'in_progress',
      label: HUDDLE_STATUS_LABELS.in_progress,
      intent: inProgress === null ? null : { statusId: inProgress.id, isCompleted: false },
      reason: inProgress === null ? 'This project has no In Progress status' : '',
    },
    {
      key: 'done',
      label: HUDDLE_STATUS_LABELS.done,
      intent: completed === null ? null : { statusId: completed.id, isCompleted: true },
      reason: completed === null ? 'This project has no Completed status' : '',
    },
  ];
}

/**
 * The checkbox on a TAP Console row, kept in step with the picker: ticking is
 * Done; un-ticking a task that sits on the completed status is Needs Alex
 * (otherwise read_huddle would still read the name 'Completed' as Done);
 * un-ticking anything else only clears the tick, as the checkbox always has.
 */
export function huddleToggleIntent(
  task: Task,
  isCompleted: boolean,
  statuses: ProjectStatus[],
): HuddleStatusIntent | null {
  const choices = huddleStatusChoices(statuses);
  if (isCompleted) return choices.find((choice) => choice.key === 'done')?.intent ?? null;
  if (statusName(task, statuses) === 'Completed' || isCompletedStatus(task, statuses)) {
    return choices.find((choice) => choice.key === 'queue')?.intent ?? null;
  }
  return null;
}

function isCompletedStatus(task: Task, statuses: ProjectStatus[]): boolean {
  if (task.status_id === null) return false;
  return statuses.some((status) => status.id === task.status_id && status.is_completed_status);
}
